/**
 * F106 (Pavel 2026-08-10): the partner's own briefing for material generation.
 *
 * "On by měl ve svém parťáckém dashboardu mít možnost někde přidat svý poznámky
 * ke klientovi, které ovlivněj generování prodejních materiálů. A měly by být
 * docela důležitý, protože to, co tam napíše parťák, už je po nějaké úvaze."
 *
 * One catalog for both sides: which notes exist, which rail step and which
 * build gate each one belongs to, and what counts as a saved note. The worker
 * enforces it, the console renders it, the machine API reads it. Neither side
 * keeps its own copy.
 */

/** The global note plus one per generated material. */
export const PARTNER_BRIEF_SCOPES = ["global", "strategy", "salespage", "emails", "webinar"] as const;
export type PartnerBriefScope = (typeof PARTNER_BRIEF_SCOPES)[number];

/** The four materials the notes steer; also the four regeneratable materials. */
export const PARTNER_BRIEF_MATERIAL_SCOPES = ["strategy", "salespage", "emails", "webinar"] as const;
export type PartnerBriefMaterialScope = (typeof PARTNER_BRIEF_MATERIAL_SCOPES)[number];

/**
 * Build gate -> note scope. The strategy step is ONE approval built from two
 * historical gates (audit + plan), so both feed from the strategy note; a
 * second mapping table would let them drift apart.
 */
const GATE_SCOPES: Record<string, PartnerBriefMaterialScope> = {
  audit: "strategy",
  plan: "strategy",
  strategy: "strategy",
  salespage: "salespage",
  emails: "emails",
  webinar: "webinar"
};

/** Deliverable kinds each material scope owns (mirrors STEP_MATERIAL_KINDS). */
export const PARTNER_BRIEF_SCOPE_KINDS: Record<PartnerBriefMaterialScope, readonly string[]> = {
  strategy: ["audit", "plan"],
  salespage: ["salespage"],
  emails: ["emails"],
  webinar: ["webinar"]
};

/** The launch gate a regeneration of this material fires. */
export const PARTNER_BRIEF_SCOPE_GATE: Record<PartnerBriefMaterialScope, string> = {
  strategy: "audit",
  salespage: "salespage",
  emails: "emails",
  webinar: "webinar"
};

/** The dfy_build_runs gate_key the material's build reports under. */
export const PARTNER_BRIEF_SCOPE_RUN_KEY: Record<PartnerBriefMaterialScope, string> = {
  strategy: "strategy",
  salespage: "salespage",
  emails: "emails",
  webinar: "webinar"
};

export function isPartnerBriefScope(value: string): value is PartnerBriefScope {
  return (PARTNER_BRIEF_SCOPES as readonly string[]).includes(value);
}

export function isPartnerBriefMaterialScope(value: string): value is PartnerBriefMaterialScope {
  return (PARTNER_BRIEF_MATERIAL_SCOPES as readonly string[]).includes(value);
}

/** Note scope of a build gate; null for a gate that generates no material. */
export function briefScopeForGate(gate: string): PartnerBriefMaterialScope | null {
  return GATE_SCOPES[gate] ?? null;
}

/** Note scope of a rail step; the step keys and the scopes coincide today. */
export function briefScopeForStep(step: string): PartnerBriefMaterialScope | null {
  return isPartnerBriefMaterialScope(step) ? step : null;
}

/**
 * Upper bound on a note. Generous on purpose: Pavel named "klidně i přepis
 * nějakého hovoru" as a legitimate input, and a truncated transcript is worse
 * than a refused one.
 */
export const PARTNER_BRIEF_MAX_CHARS = 20_000;

/**
 * A saved note, or null when the field is empty (which DELETES the note).
 * Returns `undefined` for input that is not text at all, so a malformed body
 * is refused instead of silently clearing the partner's work.
 */
export function normalizeBriefText(value: unknown): string | null | undefined {
  if (typeof value !== "string") return undefined;
  // Windows line endings and trailing whitespace only; the note is prose the
  // partner may have pasted from a call transcript, so inner structure stays.
  const text = value.replace(/\r\n/g, "\n").replace(/[ \t]+$/gm, "").trim();
  if (!text) return null;
  if (text.length > PARTNER_BRIEF_MAX_CHARS) return undefined;
  return text;
}

/**
 * The reason for a rebuild from scratch. Same shape as the F104 override
 * reason (F44 law of named reasons): a destructive-looking action always says
 * why, in writing, and the sentence survives in the audit trail. The reason is
 * ALSO an instruction for the rebuild ("ať se to celý od základu přegeneruje i
 * s tímhle komentářem"), so it reaches the build prompt.
 */
export function normalizeRegenerationReason(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const reason = value.trim().replace(/\s+/g, " ");
  if (reason.length < 3 || reason.length > 500) return null;
  return reason;
}

export interface PartnerBriefEntry {
  scope: PartnerBriefScope;
  text: string;
  revision: number;
  updatedAt: string;
  updatedBy: string;
  updatedByName: string | null;
}

export type RegenerationStatus = "queued" | "launched" | "done" | "failed";

export interface MaterialRegenerationEntry {
  id: string;
  scope: PartnerBriefMaterialScope;
  reason: string;
  status: RegenerationStatus;
  baseVersion: number | null;
  closedNoteCount: number;
  requestedAt: string;
  requestedBy: string;
  requestedByName: string | null;
  note: string | null;
}

/**
 * A launched regeneration that stopped reporting is not "still running": the
 * builder heartbeats every 60 seconds and the whole DFY chain for one material
 * finishes well inside this window. After it, the lock releases so the partner
 * is never stuck with a button that refuses forever (F44: a refusal that can
 * never clear is a dead end, not a guard). Same reasoning as the build-run
 * cutoff in dfy-build.ts, with room for a long rewrite loop.
 */
export const REGENERATION_STALE_MS = 6 * 60 * 60 * 1000;

export function isRegenerationActive(entry: { status: RegenerationStatus; requestedAt: string }, now = Date.now()): boolean {
  if (entry.status !== "queued" && entry.status !== "launched") return false;
  const started = Date.parse(entry.requestedAt);
  if (!Number.isFinite(started)) return true;
  return now - started < REGENERATION_STALE_MS;
}
